import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box, Typography, CircularProgress, Alert, Paper, Button, TextField, Divider, Avatar, Chip, Autocomplete } from '@mui/material';
import { X, FileText, Mail, Download, ChevronLeft, ChevronRight, Reply } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getFileById, downloadFile, getSubmissionWithDetails, createClient, createMetricDefinition, createMetric, getMetricDefinitions } from '@lingaai/supabase-client';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';
import { useCurrentUser } from '@lingaai/user-management';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Detect coordinate format and normalize to 0-1 fractions.
 *
 * Three formats exist in the DB:
 *   - Vision path (0-1 fractions): x1 ∈ [0, 1]
 *   - Text resolver / manual selection (0-100 percentages): x1 ∈ [0, 100]
 *
 * Detection: if any value > 1.5, treat as 0-100 percentages and divide by 100.
 */
function normalizeCoordinates(coords: {
  x1: number;
  y1: number;
  width: number;
  height: number;
}): { x1: number; y1: number; width: number; height: number } | null {
  if (coords.x1 == null || coords.y1 == null || !coords.width || !coords.height) {
    return null;
  }

  const isPercentage =
    coords.x1 > 1.5 || coords.y1 > 1.5 || coords.width > 1.5 || coords.height > 1.5;

  let x1 = isPercentage ? coords.x1 / 100 : coords.x1;
  let y1 = isPercentage ? coords.y1 / 100 : coords.y1;
  let w = isPercentage ? coords.width / 100 : coords.width;
  let h = isPercentage ? coords.height / 100 : coords.height;

  // Clamp to [0, 1]
  x1 = Math.max(0, Math.min(1, x1));
  y1 = Math.max(0, Math.min(1, y1));
  w = Math.max(0.005, Math.min(1 - x1, w));
  h = Math.max(0.002, Math.min(1 - y1, h));

  // Reject if origin is off-page
  if (x1 >= 1 || y1 >= 1) return null;

  return { x1, y1, width: w, height: h };
}

interface DocumentViewerModalProps {
  open: boolean;
  onClose: () => void;
  fileId: string | null;
  submissionId: string | null;
  emailBody?: string;
  highlightMetricId?: string | null;
  fundId?: string | null;
  initialPage?: number;
}

export function DocumentViewerModal({ open, onClose, fileId, emailBody, submissionId, highlightMetricId, fundId, initialPage }: DocumentViewerModalProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pageWidth, setPageWidth] = useState<number>(800);
  const [pageHeight, setPageHeight] = useState<number>(0);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState<any>(null);
  const [comment, setComment] = useState('');
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);
  const [newMetricModalOpen, setNewMetricModalOpen] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectionCoords, setSelectionCoords] = useState<any>(null);
  const [newMetricName, setNewMetricName] = useState('');
  const [newMetricValue, setNewMetricValue] = useState('');
  const [selectedDefinition, setSelectedDefinition] = useState<any>(null);

  const [containerWidth, setContainerWidth] = useState<number>(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  // Callback ref: fires when the PDF container DOM element mounts/unmounts
  const pdfContainerRef = useCallback((node: HTMLDivElement | null) => {
    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (node) {
      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          setContainerWidth(entry.contentRect.width);
        }
      });
      observer.observe(node);
      observerRef.current = observer;
    }
  }, []);

  // Never render wider than 960px, never narrower than 300px, with 24px padding
  const effectivePageWidth = containerWidth > 0
    ? Math.min(960, Math.max(300, containerWidth - 24))
    : 800;

  const { data: currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  const { data: submission } = useQuery({
    queryKey: ['submission-details', submissionId],
    queryFn: () => getSubmissionWithDetails(submissionId!),
    enabled: !!submissionId && open,
  });

  const { data: allMetricDefinitions = [] } = useQuery({
    queryKey: ['metric-definitions', fundId],
    queryFn: () => getMetricDefinitions(fundId!),
    enabled: !!fundId && open,
  });

  const existingMetricDefinitionIds = new Set(submission?.metrics?.map((m: any) => m.metric_definition_id) || []);

  const metricDefinitions = allMetricDefinitions.filter((def: any) => !existingMetricDefinitionIds.has(def.id));

  const createCommentMutation = useMutation({
    mutationFn: async ({ metricId, commentText, replyId }: { metricId: string; commentText: string; replyId?: string | null }) => {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('metric_comments')
        .insert({
          metric_id: metricId,
          comment: commentText,
          reply_id: replyId || null,
          tenant_id: currentUser?.tenant_id,
          created_by: currentUser?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submission-details', submissionId] });
    },
  });

  const createMetricMutation = useMutation({
    mutationFn: async () => {
      if (!currentUser?.tenant_id) throw new Error('Missing tenant_id');

      const effectiveFundId = fundId || submission?.portfolio_companies?.fund_id;
      if (!effectiveFundId) throw new Error('Missing fund_id');

      let definitionId: string;

      if (selectedDefinition?.id) {
        definitionId = selectedDefinition.id;
      } else {
        if (!newMetricName.trim()) throw new Error('Missing metric name');
        const maxOrder = submission.metrics?.reduce((max: number, m: any) => Math.max(max, m.metric_definitions?.display_order || 0), 0) || 0;

        const definition = await createMetricDefinition({
          tenant_id: currentUser.tenant_id,
          fund_id: effectiveFundId,
          name: newMetricName.trim(),
          description: 'Manually created from PDF selection',
          data_type: 'text',
          is_required: false,
          display_order: maxOrder + 1,
        });
        definitionId = definition.id;
      }

      return await createMetric({
        tenant_id: currentUser.tenant_id,
        submission_id: submissionId!,
        metric_definition_id: definitionId,
        value: newMetricValue.trim() as any,
        source_type: 'file',
        source_file_id: fileId!,
        source_location: `Page ${pageNumber}, "${selectedText}"`,
        source_coordinates: {
          pageNumber: selectionCoords.pageNumber,
          boundingRect: selectionCoords,
          rects: [selectionCoords],
        },
        source_page_number: pageNumber,
        confidence: 1.0,
        extraction_method: 'text', // Manual selection from text-extracted document
        extracted_by: 'manual',
        reviewed: true,
        reviewed_by: currentUser?.id || null,
        reviewed_at: new Date().toISOString(),
        created_by: currentUser?.id!,
        updated_by: currentUser?.id || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['submission-details', submissionId] });
      handleCloseNewMetricModal();
      setTimeout(() => window.getSelection()?.removeAllRanges(), 100);
    },
  });

  const metrics = submission?.metrics?.filter((m: any) => m.source_file_id === fileId && m.source_coordinates) || [];

  // Set initial page when modal opens with a metric to highlight
  useEffect(() => {
    if (open && initialPage && initialPage !== pageNumber) {
      setPageNumber(initialPage);
    }
  }, [open, initialPage]);

  useEffect(() => {
    if (highlightMetricId && metrics.length > 0) {
      const metric = metrics.find((m: any) => m.id === highlightMetricId);
      if (metric?.source_coordinates?.pageNumber) {
        setPageNumber(metric.source_coordinates.pageNumber);
      }
    }
  }, [highlightMetricId, metrics]);

  // Text selection listener
  useEffect(() => {
    const handleTextSelection = () => {
      const selection = window.getSelection();
      const selectedText = selection?.toString().trim();

      if (!selectedText || selectedText.length === 0) return;
      if (!pageWidth || !pageHeight) return;

      const range = selection?.getRangeAt(0);
      if (!range) return;

      const container = document.getElementById('pdf-page-container');
      if (!container?.contains(range.commonAncestorContainer)) return;

      const selectionRect = range.getBoundingClientRect();
      const pageContainer = container.getBoundingClientRect();

      const relativeX = selectionRect.left - pageContainer.left;
      const relativeY = selectionRect.top - pageContainer.top;
      const relativeWidth = selectionRect.width;
      const relativeHeight = selectionRect.height;

      const left = relativeX / pageWidth;
      const top = relativeY / pageHeight;
      const width = relativeWidth / pageWidth;
      const height = relativeHeight / pageHeight;

      setSelectedText(selectedText);
      setSelectionCoords({
        x1: left,
        y1: top,
        x2: left + width,
        y2: top + height,
        width: width,
        height: height,
        pageNumber: pageNumber,
      });
      setNewMetricModalOpen(true);
    };

    document.addEventListener('mouseup', handleTextSelection);
    return () => document.removeEventListener('mouseup', handleTextSelection);
  }, [pageWidth, pageHeight, pageNumber]);

  // Fetch file data if fileId is provided and not email_body
  const {
    data: file,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['file', fileId],
    queryFn: () => getFileById(fileId!),
    enabled: !!fileId && fileId !== 'email_body',
  });

  // Fetch PDF blob for rendering
  const { data: blob } = useQuery({
    queryKey: ['file-blob', file?.file_path, open],
    queryFn: async () => {
      if (!file?.file_path) return null;
      const blob = await downloadFile(file.file_path);
      setPdfBlob(blob);
      return blob;
    },
    enabled: !!file?.file_path && file?.mime_type === 'application/pdf' && open,
    staleTime: 0,
    gcTime: 0,
  });

  const isEmail = fileId === 'email_body';
  const isPDF = file?.mime_type === 'application/pdf';
  const displayContent = isEmail ? emailBody : file?.extracted_text;
  const displayTitle = isEmail ? 'Email Body' : file?.file_name || 'Document';

  const handleMetricClick = (metricId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const metric = metrics.find((m: any) => m.id === metricId);
    if (metric) {
      setSelectedMetric(metric);
      setCommentModalOpen(true);
    }
  };

  const handleCloseCommentModal = () => {
    setCommentModalOpen(false);
    setSelectedMetric(null);
    setComment('');
    setReplyToCommentId(null);
  };

  const handleSaveComment = async () => {
    if (!selectedMetric?.id || !comment.trim()) return;

    try {
      await createCommentMutation.mutateAsync({
        metricId: selectedMetric.id,
        commentText: comment.trim(),
        replyId: replyToCommentId,
      });
      handleCloseCommentModal();
    } catch (error) {
      console.error('Failed to save comment:', error);
    }
  };

  const handleCloseNewMetricModal = () => {
    setNewMetricModalOpen(false);
    setSelectedText('');
    setSelectionCoords(null);
    setNewMetricName('');
    setNewMetricValue('');
    setSelectedDefinition(null);
  };

  const handleSaveNewMetric = async () => {
    if ((!selectedDefinition && !newMetricName.trim()) || !newMetricValue.trim()) return;

    try {
      await createMetricMutation.mutateAsync();
    } catch (error) {
      console.error('Failed to create metric:', error);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  const onPageLoadSuccess = async (page: any) => {
    // Calculate scale to match the rendered page width
    const unscaledViewport = page.getViewport({ scale: 1 });
    const scale = effectivePageWidth / unscaledViewport.width;
    const scaledViewport = page.getViewport({ scale });

    setPageWidth(scaledViewport.width);
    setPageHeight(scaledViewport.height);

    // Manually render text layer for selection
    const textLayerDiv = document.getElementById('pdf-text-layer');
    if (textLayerDiv) {
      textLayerDiv.innerHTML = '';
      textLayerDiv.style.width = scaledViewport.width + 'px';
      textLayerDiv.style.height = scaledViewport.height + 'px';

      // Set CSS variables required by TextLayer (pdfjs v5) - use actual scale
      textLayerDiv.style.setProperty('--total-scale-factor', scale.toString());
      textLayerDiv.style.setProperty('--scale-round-x', '1px');
      textLayerDiv.style.setProperty('--scale-round-y', '1px');

      const textContent = await page.getTextContent();

      // Use TextLayer class (pdfjs v5 API, not deprecated renderTextLayer)
      const textLayer = new (pdfjs as any).TextLayer({
        textContentSource: textContent,
        container: textLayerDiv,
        viewport: scaledViewport,
      });
      await textLayer.render();
    }
  };

  const handleDownload = async () => {
    // Download email body as text file
    if (isEmail && emailBody) {
      const blob = new Blob([emailBody], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'email-body.txt';
      a.click();
      URL.revokeObjectURL(url);
      return;
    }

    // Download actual file
    if (!file?.file_path || !blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.file_name;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  // Reset PDF state when modal closes
  useEffect(() => {
    if (!open) {
      setPdfBlob(null);
      setPageNumber(1);
      setNumPages(0);
      setPageWidth(effectivePageWidth || 800);
      setPageHeight(0);
    }
  }, [open]);

  if (!open) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        height: '90vh',
        position: 'fixed',
        right: { xs: '100%', sm: 'calc(600px + 8px)', md: 'calc(800px + 8px)' },
        left: 8,
        top: '50%',
        transform: 'translateY(-50%)',
        margin: 0,
        maxWidth: 'none',
        width: {
          xs: 'calc(100% - 16px)',
          sm: 'calc(100vw - 600px - 16px)',
          md: 'calc(100vw - 800px - 16px)',
        },
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEmail ? <Mail size={ICON_SIZES.md} strokeWidth={ICON_STROKE} /> : <FileText size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />}
          <Typography variant="h6">{displayTitle}</Typography>
          {isPDF && numPages > 0 && (
            <Typography variant="body2" color="text.secondary">
              ({numPages} pages)
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {file && (
            <IconButton onClick={handleDownload} size="small" title="Download">
              <Download size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
            </IconButton>
          )}
          <IconButton onClick={onClose} size="small">
            <X size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error instanceof Error ? error.message : 'Failed to load document'}</Alert>
          </Box>
        )}

        {/* PDF Viewer */}
        {isPDF && (
          <Box
            ref={pdfContainerRef}
            sx={{
              height: '100%',
              overflow: 'auto',
              bgcolor: 'grey.100',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {!pdfBlob || containerWidth === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : (<>
            <Document
              key={`${fileId}-${open}`}
              file={pdfBlob}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              }
            >
              <Box sx={{ position: 'relative' }} id="pdf-page-container">
                <Page pageNumber={pageNumber} renderTextLayer={false} renderAnnotationLayer={false} width={effectivePageWidth} onLoadSuccess={onPageLoadSuccess} />

                {/* Manual text layer for selection */}
                <Box
                  id="pdf-text-layer"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    overflow: 'hidden',
                    opacity: 1,
                    lineHeight: 1.0,
                    '& > span': {
                      color: 'transparent',
                      position: 'absolute',
                      whiteSpace: 'pre',
                      cursor: 'text',
                      transformOrigin: '0% 0%',
                    },
                    '& span::selection': {
                      background: 'rgba(59, 130, 246, 0.5)',
                      color: 'transparent',
                    },
                  }}
                />

                {metrics.length > 0 && pageHeight > 0 && (
                  <svg
                    viewBox={`0 0 ${pageWidth} ${pageHeight}`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      pointerEvents: 'none',
                    }}
                  >
                    {metrics
                      .filter((m: any) => m.source_coordinates?.pageNumber === pageNumber)
                      .map((metric: any) => {
                        const rawCoords = metric.source_coordinates?.boundingRect;
                        if (!rawCoords) return null;

                        const normalized = normalizeCoordinates(rawCoords);
                        if (!normalized) return null;

                        const isHighlighted = metric.id === highlightMetricId;
                        const isManual = metric.extracted_by !== 'agent';
                        const commentCount = metric.metric_comments?.length || 0;

                        // Convert normalized coordinates (0-1 range) to pixels
                        // Add padding for better visibility
                        const padX = 8;
                        const padY = 4;
                        const x = Math.max(0, normalized.x1 * pageWidth - padX);
                        const y = Math.max(0, normalized.y1 * pageHeight - padY);
                        const width = Math.min(pageWidth - x, normalized.width * pageWidth + padX * 2);
                        const height = Math.min(pageHeight - y, normalized.height * pageHeight + padY * 2);

                        return (
                          <g key={metric.id}>
                            <rect
                              x={x}
                              y={y}
                              width={width}
                              height={height}
                              fill={isHighlighted ? (isManual ? 'rgba(249, 115, 22, 0.25)' : 'rgba(34, 197, 94, 0.25)') : isManual ? 'rgba(249, 115, 22, 0.15)' : 'rgba(34, 197, 94, 0.15)'}
                              stroke={isManual ? '#f97316' : '#22c55e'}
                              strokeWidth={isHighlighted ? '3' : '2'}
                              strokeDasharray={isHighlighted ? '5,3' : undefined}
                              style={{ cursor: 'pointer', transition: 'all 0.2s', pointerEvents: 'auto' }}
                              onClick={(e) => handleMetricClick(metric.id, e)}
                            />
                            {commentCount > 0 && <circle cx={x + width} cy={y} r="5" fill="#3FAD9F" style={{ pointerEvents: 'none' }} />}
                          </g>
                        );
                      })}
                  </svg>
                )}
              </Box>
            </Document>

            {/* Page Navigation */}
            {numPages > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  bgcolor: 'background.paper',
                  borderTop: 1,
                  borderColor: 'divider',
                  position: 'sticky',
                  bottom: 0,
                  width: '100%',
                  justifyContent: 'center',
                }}
              >
                <Button
                  size="small"
                  onClick={() => setPageNumber((prev) => Math.max(1, prev - 1))}
                  disabled={pageNumber <= 1}
                  startIcon={<ChevronLeft size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />}
                >
                  Previous
                </Button>
                <Typography variant="body2">
                  Page {pageNumber} of {numPages}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setPageNumber((prev) => Math.min(numPages, prev + 1))}
                  disabled={pageNumber >= numPages}
                  endIcon={<ChevronRight size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />}
                >
                  Next
                </Button>
              </Box>
            )}
            </>)}
          </Box>
        )}

        {/* Text Viewer (for non-PDF or fallback) */}
        {!isPDF && displayContent && (
          <Paper
            sx={{
              height: '100%',
              overflow: 'auto',
              p: 3,
              bgcolor: 'grey.50',
              borderRadius: 0,
            }}
          >
            <Typography
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                m: 0,
              }}
            >
              {displayContent}
            </Typography>
          </Paper>
        )}

        {!isLoading && !error && !displayContent && !pdfBlob && (
          <Box sx={{ p: 3 }}>
            <Alert severity="info">No content available for this document</Alert>
          </Box>
        )}
      </DialogContent>

      {/* Comment Modal */}
      <Dialog open={commentModalOpen} onClose={handleCloseCommentModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">Comments</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedMetric?.metric_definitions?.name || 'Unknown Metric'}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseCommentModal} size="small">
              <X size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedMetric && (
            <>
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Current Value
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {typeof selectedMetric.value === 'object' ? JSON.stringify(selectedMetric.value) : selectedMetric.value}
                </Typography>
                {selectedMetric.source_location && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
                      Source
                    </Typography>
                    <Typography variant="body2">{selectedMetric.source_location}</Typography>
                  </>
                )}
              </Box>

              {/* Existing Comments */}
              {selectedMetric.metric_comments && selectedMetric.metric_comments.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Comments ({selectedMetric.metric_comments.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {selectedMetric.metric_comments
                      .filter((c: any) => !c.reply_id)
                      .map((comment: any) => {
                        const replies = selectedMetric.metric_comments.filter((r: any) => r.reply_id === comment.id);
                        return (
                          <Box key={comment.id}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                                  {comment.users?.full_name?.charAt(0) || comment.users?.email?.charAt(0) || '?'}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="subtitle2">{comment.users?.full_name || comment.users?.email || 'Unknown'}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {comment.comment}
                                  </Typography>
                                  <Button size="small" startIcon={<Reply size={14} />} onClick={() => setReplyToCommentId(comment.id)} sx={{ mt: 1, textTransform: 'none' }}>
                                    Reply
                                  </Button>
                                </Box>
                              </Box>
                            </Paper>

                            {/* Replies */}
                            {replies.length > 0 && (
                              <Box sx={{ ml: 5, mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {replies.map((reply: any) => (
                                  <Paper key={reply.id} variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.main', fontSize: '0.75rem' }}>
                                        {reply.users?.full_name?.charAt(0) || reply.users?.email?.charAt(0) || '?'}
                                      </Avatar>
                                      <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <Typography variant="caption" fontWeight="medium">
                                            {reply.users?.full_name || reply.users?.email || 'Unknown'}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {new Date(reply.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                          </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                                          {reply.comment}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Paper>
                                ))}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* New Comment Input */}
              <Box>
                {replyToCommentId && (
                  <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="Replying to comment" size="small" onDelete={() => setReplyToCommentId(null)} color="primary" variant="outlined" />
                  </Box>
                )}
                <TextField
                  label={replyToCommentId ? 'Reply' : 'New Comment'}
                  multiline
                  rows={3}
                  fullWidth
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={replyToCommentId ? 'Write your reply...' : 'Add a comment or note about this metric...'}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCommentModal} disabled={createCommentMutation.isPending}>
            Close
          </Button>
          <Button onClick={handleSaveComment} variant="contained" disabled={!comment.trim() || createCommentMutation.isPending}>
            {createCommentMutation.isPending ? 'Saving...' : replyToCommentId ? 'Post Reply' : 'Post Comment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Metric Modal */}
      <Dialog open={newMetricModalOpen} onClose={handleCloseNewMetricModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Create New Metric</Typography>
            <IconButton onClick={handleCloseNewMetricModal} size="small">
              <X size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Selected Text
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              {selectedText}
            </Typography>
          </Box>

          <Autocomplete
            options={[{ id: 'create-new', name: '+ Create New Metric', isCreateNew: true }, ...metricDefinitions]}
            getOptionLabel={(option: any) => option.name || ''}
            value={selectedDefinition}
            onChange={(_, newValue: any) => {
              if (newValue?.isCreateNew) {
                setSelectedDefinition(null);
                setNewMetricName('');
              } else {
                setSelectedDefinition(newValue);
                setNewMetricName('');
              }
            }}
            renderInput={(params) => <TextField {...params} label="Metric Definition" placeholder="Select existing or create new" autoFocus />}
            sx={{ mb: 2 }}
          />

          {!selectedDefinition && (
            <TextField
              label="New Metric Name"
              fullWidth
              value={newMetricName}
              onChange={(e) => setNewMetricName(e.target.value)}
              placeholder="e.g., Revenue, Gross Margin, etc."
              autoFocus
              sx={{ mb: 2 }}
            />
          )}

          <TextField label="Metric Value" fullWidth value={newMetricValue} onChange={(e) => setNewMetricValue(e.target.value)} placeholder="Enter the value for this metric" />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {selectedDefinition ? 'This will add a metric to the submission using the selected definition.' : 'This will create a new metric definition and add this metric to the submission.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewMetricModal} disabled={createMetricMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSaveNewMetric} variant="contained" disabled={(!selectedDefinition && !newMetricName.trim()) || !newMetricValue.trim() || createMetricMutation.isPending}>
            {createMetricMutation.isPending ? 'Creating...' : 'Create Metric'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
